import { listTablePrefs } from 'app/constants'
import InferActionParams from 'core/actions/InferActionParams'
import CreateButton from 'core/components/buttons/CreateButton'
import DocumentMeta from 'core/components/DocumentMeta'
import { DateAndTime } from 'core/components/listTable/cells/DateCell'
import ListContainer from 'core/containers/ListContainer'
import { createGridStatusCell, StatusCellModel } from 'core/elements/grid/cells/GridStatusCell'
import { GridViewColumn } from 'core/elements/grid/Grid'
import getGridDialogButton from 'core/elements/grid/helpers/getGridDialogButton'
import { SortingState } from 'core/elements/grid/hooks/useGridSorting'
import useGlobalParams from 'core/hooks/useGlobalParams'
import useListAction from 'core/hooks/useListAction'
import { createUsePrefParamsHook } from 'core/hooks/useParams'
import useScopedPreferences from 'core/session/useScopedPreferences'
import { isReadonlyUser } from 'core/utils/helpers'
import AddNodeGroupsModal from 'k8s/components/capacity-and-health/AddNodeGroupsModal'
import { pick } from 'ramda'
import React, { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { isNilOrEmpty } from 'utils/fp'
import EditClusterNodeGroupsModal from '../clusters-dashboard/EditClusterNodeGroupsModal'
import EditGpuConfigurations from '../clusters-dashboard/EditGpuCofigurations'
import { listKaapiClusters } from '../kaapi/clusters/actions'
import { kaapiClustersOverviewSelector } from '../kaapi/clusters/selectors'
import { listKaapiMachineDeployments } from '../kaapi/machine-deployment/actions'
import { IMachineDeploymentSelector } from '../kaapi/machine-deployment/model'
import { kaapiMachineDeploymentsForClusterSelector } from '../kaapi/machine-deployment/selectors'
import { listKaapiMachines } from '../kaapi/machines/actions'
import { kaapiMachinesForClusterSelector } from '../kaapi/machines/selectors'
import DeleteNodeGroupsDialog from './DeleteNodeGroupsDialog'
import Text from 'core/elements/Text'

type ActionParams = InferActionParams<typeof listKaapiMachines>
type Params = ActionParams & {}

const defaultParams: Params & SortingState = {
  orderBy: 'metadata.creationTimestamp',
  orderDirection: 'desc',
}

const usePrefParams = createUsePrefParamsHook('Machine Deployments', listTablePrefs)

export type NodeGroupSelector = IMachineDeploymentSelector

const Phase = {
  Provisioning: 'Provisioning',
  Provisioned: 'Provisioned',
  Running: 'Running',
  Failed: 'Failed',
  ScalingUp: 'ScalingUp',
  ScalingDown: 'ScalingDown',
  Deleting: 'Deleting',
  Unknown: 'Unknown',
}

const getPhaseStatus = (phase): StatusCellModel => {
  switch (phase) {
    case Phase.Running:
    case Phase.Provisioned:
      return { variant: 'success', label: phase }
    case Phase.ScalingUp:
    case Phase.ScalingDown:
    case Phase.Provisioning:
      return { variant: 'warning', label: phase }
    case Phase.Failed:
    case Phase.Deleting:
      return { variant: 'error', label: phase }
    default:
      return { variant: 'unknown', label: Phase.Unknown }
  }
}
const cellFormatFn = (value) => (isNilOrEmpty(value) ? '-' : value)
const columns = (isBYOHCluster: boolean): GridViewColumn<any>[] => [
  {
    key: 'metadata.name',
    label: 'Name',
    // CellComponent: NodeGroupNameCell,
    // memoizeCell: false,
  },
  {
    key: 'status.phase',
    label: 'Status',
    CellComponent: createGridStatusCell({
      dataFn: (phase): StatusCellModel => {
        return getPhaseStatus(phase)
      },
    }),
  },
  ...(isBYOHCluster
    ? []
    : [
        {
          key: 'openStackMachineTemplate.spec.template.spec.flavor',
          label: 'Flavor',
        },
      ]),
  {
    key: 'spec.autoscaling',
    label: 'Auto Scaling',
  },
  {
    key: 'spec.template.spec.version',
    label: 'K8s Version',
    formatFn: cellFormatFn,
  },
  {
    key: 'metadata.creationTimestamp',
    label: 'Created',
    CellComponent: DateAndTime,
  },
  {
    key: 'spec.replicas',
    label: 'Desired Replicas',
    formatFn: cellFormatFn,
  },
  {
    key: 'status.readyReplicas',
    label: 'Ready Replicas',
    formatFn: cellFormatFn,
  },
  {
    key: 'spec.autoscalingMaxNodes',
    label: 'Max Nodes',
    CellComponent: (props) => {
      return (
        <Text variant="body2">
          {props?.item?.spec?.autoscaling === 'Disabled' ? '-' : props?.value}
        </Text>
      )
    },
  },
]

export default function ListNodeGroups() {
  const { allParams: params, getParamsUpdater } = useGlobalParams(usePrefParams, defaultParams)
  const [open, setOpen] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [selectedNodeGroup, setSelectedNodeGroup] = useState(null)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showEditGpuConfigModal, setShowEditGpuConfigModal] = useState(false)
  const { prefs: k8sPluginGlobalPerfs } = useScopedPreferences('k8sPluginGlobalParams')

  // Load the list of clusters
  const { loading: loadingKaapiClusters, reload: reloadKaapiClusters } = useListAction(
    listKaapiClusters,
  )
  const clusters = useSelector(kaapiClustersOverviewSelector)
  const cluster = clusters?.find(
    (cluster) => cluster.metadata.name === k8sPluginGlobalPerfs?.cluster,
  )

  // Fetch Worker Nodes
  const { loading: loadingKaapiMachines } = useListAction(listKaapiMachines)
  const machinesSelector = kaapiMachinesForClusterSelector({
    clusterName: k8sPluginGlobalPerfs.cluster,
  })
  const machines = useSelector(machinesSelector) || []

  // Fetch Node Groups
  const { loading: loadingMachineDeployments, reload: reloadMachineDeployments } = useListAction(
    listKaapiMachineDeployments,
  )
  const machineDeploymentSelector = kaapiMachineDeploymentsForClusterSelector({
    clusterName: k8sPluginGlobalPerfs.cluster,
  })
  const machineDeployments = useSelector(machineDeploymentSelector)

  // Get the machine deployments that have gpu nodes
  const machineDeploymentGPUDetails = useMemo(() => {
    return machineDeployments.reduce((acc, md) => {
      return {
        ...acc,
        [md?.metadata?.name]:
          machines.find((machine) => {
            return (
              machine?.machineDeployment?.metadata?.name === md?.metadata?.name &&
              !!machine?.gpu.enabled
            )
          })?.gpu || {},
      }
    }, {})
  }, [machineDeployments, machines])

  // Check if this is the last running node in the cluster
  const isLastRunningNode: boolean = useMemo(
    () => machines?.length === 1 && machines[0]?.status.phase === 'Running',
    [machines],
  )

  const k8sVersion = useMemo(() => {
    if (!cluster?.hostedControlPlane?.spec?.version) return ''

    return cluster?.hostedControlPlane?.spec?.version
  }, [cluster])

  const batchActions = [
    ...(cluster?.isBYOHCluster
      ? []
      : [
          {
            icon: 'edit',
            label: 'Edit',
            handleAction: (nodegroup) => {
              if (nodegroup?.length === 0) return
              setSelectedNodeGroup(nodegroup[0])
              setShowEditModal(true)
            },
          },
          {
            label: 'Delete',
            icon: 'trash-alt',
            BatchActionButton: getGridDialogButton(DeleteNodeGroupsDialog, {
              isLastRunningNode,
              reloadMachineDeployments,
            }),
          },
        ]),
    {
      label: 'Edit GPU Configuration',
      icon: 'edit',
      handleAction: (nodegroups) => {
        if (nodegroups?.length === 0) return
        setSelectedNodeGroup(nodegroups[0])
        setShowEditGpuConfigModal(true)
      },
      cond: (nodegroups) =>
        nodegroups?.length === 1 &&
        !!machineDeploymentGPUDetails?.[nodegroups[0]?.metadata?.name]?.enabled,
      disabledTooltip: 'Node group does not have GPU nodes',
    },
  ]

  const rowMenuItems = [
    ...(cluster?.isBYOHCluster
      ? []
      : [
          {
            label: 'Delete',
            icon: 'trash-alt',
            handleClick: (nodegroup) => {
              setSelectedNodeGroup(nodegroup)
              setShowDeleteDialog(true)
            },
            refreshAfterSuccess: true,
            hideIfDisabled: true,
          },
          {
            label: 'Edit',
            icon: 'edit',
            handleClick: (nodegroup) => {
              setSelectedNodeGroup(nodegroup)
              setShowEditModal(true)
            },
            refreshAfterSuccess: true,
            hideIfDisabled: true,
          },
        ]),
    {
      label: 'Edit GPU Configuration',
      icon: 'edit',
      handleClick: (nodegroup) => {
        setSelectedNodeGroup(nodegroup)
        setShowEditGpuConfigModal(true)
      },
      refreshAfterSuccess: true,
      hideIfDisabled: true,
      cond: (nodegroup) => !!machineDeploymentGPUDetails?.[nodegroup?.metadata?.name]?.enabled,
    },
  ]

  const showAddNodeGroupButton = useMemo(() => !isReadonlyUser() && !cluster?.isBYOHCluster, [
    cluster,
    isReadonlyUser,
  ])

  return (
    <>
      <DocumentMeta title="Capi Node Groups Overview" data-testid="node-groups-overview" />
      {open && (
        <AddNodeGroupsModal
          onClose={() => setOpen(false)}
          k8sVersion={k8sVersion}
          data-testid="add-node-group-modal"
        />
      )}
      {showEditModal && (
        <EditClusterNodeGroupsModal
          onClose={() => setShowEditModal(false)}
          rows={[selectedNodeGroup]}
          data-testid="edit-node-group-modal"
        />
      )}
      {showEditGpuConfigModal && (
        <EditGpuConfigurations
          onClose={() => setShowEditGpuConfigModal(false)}
          rows={[selectedNodeGroup]}
          data-testid="edit-gpu-configurations-modal"
          clusterName={cluster?.metadata?.name}
          gpuDetails={machineDeploymentGPUDetails[selectedNodeGroup?.metadata?.name]}
        />
      )}
      {showDeleteDialog && (
        <DeleteNodeGroupsDialog
          rows={[selectedNodeGroup]}
          onClose={() => setShowDeleteDialog(false)}
          isLastRunningNode={isLastRunningNode}
          reloadMachineDeployments={reloadMachineDeployments}
          data-testid="delete-node-group-dialog"
        />
      )}
      <ListContainer<any, any>
        searchTargets={['metadata.name', 'status.phase']}
        uniqueIdentifier="uid"
        loading={loadingMachineDeployments}
        loadingMessage="Loading..."
        onRefresh={reloadMachineDeployments}
        data={machineDeployments}
        columns={columns(cluster?.isBYOHCluster)}
        {...pick(listTablePrefs, params)}
        getParamsUpdater={getParamsUpdater}
        label="Node Groups"
        showItemsCountInLabel
        showBreadcrumbs={false}
        batchActions={batchActions}
        extraToolbarContent={
          showAddNodeGroupButton && (
            <CreateButton
              icon="add"
              onClick={() => setOpen(true)}
              data-testid="add-node-group-button"
            >
              Add Node Group
            </CreateButton>
          )
        }
        rowMenuItems={rowMenuItems}
        showRowMenuForSingleRowActions
        data-testid="node-groups-list-container"
      />
    </>
  )
}
